/**
 * restaurantCache.js
 *
 * Safe persistence for the `restaurant` AsyncStorage key — the active store
 * document, read by ~40 call sites across POS, Management, Service and Warehouse.
 *
 * The key used to receive `/api/user/me → restaurants[0]` VERBATIM on every
 * login, every store switch and every background system-data refresh. That is a
 * Mongo document with five unbounded append-only ledgers on it
 * (`transactionHistory`, `aiCreditTransactions`, `voipCreditTransactions`,
 * `mailCreditTransactions`, `smsCreditTransactions`) plus stored e-mail/invoice/
 * quote HTML templates and per-place NTAK PEM certificates. On a long-lived
 * store those ledgers alone run to megabytes, and the write crashed the web
 * build with:
 *
 *   QuotaExceededError: Failed to execute 'setItem' on 'Storage':
 *   Setting the value of 'restaurant' exceeded the quota.
 *
 * Two independent problems, fixed together:
 *
 *  1. **The payload has no business being that big.** Every field dropped below
 *     is referenced ZERO times anywhere in this app — they are admin-panel and
 *     billing concerns that merely happen to live on the same document. Pruning
 *     them is what makes the write small on *every* platform, not just web:
 *     on a low-RAM Android terminal the old path serialized and handed a
 *     multi-MB string to SQLite on every sync cycle, which is a GC spike and a
 *     visible stall, quota or no quota.
 *  2. **A cache write must never be fatal.** Everything goes through
 *     `safeSetItem`, which skips identical rewrites, evicts re-fetchable caches
 *     and retries, and degrades to a smaller projection before giving up — and
 *     never throws into login / the background task.
 *
 * Deliberately a DROP list, not a keep list: `restaurant` is read in too many
 * places (and by sub-object spread) for a whitelist to be safe. Anything not
 * named here survives untouched, so existing readers cannot regress.
 *
 * @module helpers/restaurantCache
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

import { safeSetItem } from './storageQuota';

/** AsyncStorage key holding the active store document. @type {string} */
export const RESTAURANT_KEY = 'restaurant';

/**
 * Top-level store fields dropped before caching.
 *
 * Every entry was verified to have **no reader in this app** (`git grep` across
 * `app/**` and `App.jsx`); they exist on the document for the admin panel and the
 * billing back office, which fetch the store through `Restaurant.getRestaurant`
 * rather than from this cache.
 *
 * Grouped by why they are dropped:
 *  - **Unbounded ledgers** — append one row per credit spend, forever. The
 *    dominant cost and the direct cause of the quota crash.
 *  - **Stored templates** — full HTML/EJS bodies for e-mail, invoice and quote
 *    rendering; all rendering happens server-side.
 *  - **Secrets** — payment tokens and NTAK material that should not sit in a
 *    browser's localStorage at all (it is readable by any script on the origin).
 *  - **Back-office settings** — admin-only configuration blocks.
 *
 * @type {string[]}
 */
export const RESTAURANT_DROP_KEYS = [
    // Unbounded append-only ledgers.
    'transactionHistory',
    'aiCreditTransactions',
    'voipCreditTransactions',
    'mailCreditTransactions',
    'smsCreditTransactions',
    'debts',
    'currencyChanges',
    'ratings',
    'partnerRequests',
    // Stored template bodies (server-side rendering only).
    'emailTemplates',
    'invoiceTemplates',
    'quotePdfTemplate',
    'emailBranding',
    'emailSenderIdentities',
    'emailWarmup',
    // Secrets / payment instruments.
    'stripeAccount',
    'cardToken',
    'registeredCard',
    'recurringPayment',
    'navConnection',
    // Back-office-only settings.
    'aiAssistantSettings',
    'dailyClosingSettings',
    'projectSettings',
    'retentionDefaults',
    'convertedFromLead',
];

/**
 * Per-place fields dropped before caching.
 *
 * `places[]` itself is kept in full — `SettingsPlaceChooser` hands a place
 * straight from this cache to the `restaurantPlace` key, and `TableMapScreen`
 * reads `floors[].elements[]` off that — but the NTAK material is PEM key /
 * certificate / CSR blobs that no screen reads and that must not be mirrored
 * into web storage.
 *
 * @type {string[]}
 */
export const PLACE_DROP_KEYS = [
    'ntakCert',
    'ntakKey',
    'ntakCertRequest',
];

/**
 * Per-place fields dropped only under storage pressure (degradation level 2).
 *
 * `floors` carries the table map (every table's geometry per floor). Losing it
 * downgrades the table-map screen until the next successful sync, which is a far
 * better outcome than having no cached store at all.
 *
 * @type {string[]}
 */
export const PLACE_PRESSURE_DROP_KEYS = [
    'floors',
    'zipCodes',
];

/**
 * Remove a set of keys from a plain object, without mutating the input.
 *
 * @param {Object} source Object to copy.
 * @param {string[]} dropKeys Keys to omit.
 * @returns {Object} Shallow copy minus `dropKeys`.
 */
const omit = (source, dropKeys) => {
    const next = {};

    Object.keys(source).forEach((key) => {
        if (!dropKeys.includes(key)) {
            next[key] = source[key];
        }
    });

    return next;
};

/**
 * Project a raw store document down to what this app actually caches.
 *
 * @param {Object|null|undefined} restaurant Raw store document (`/me → restaurants[0]`).
 * @param {Object} [options] Projection options.
 * @param {boolean} [options.underPressure=false] Also drop `PLACE_PRESSURE_DROP_KEYS`
 *   from every place (table maps, delivery ZIP lists). Used by the degradation
 *   ladder when the full projection still does not fit.
 * @returns {Object|null} Slimmed copy, or `null` for non-object input.
 */
export const slimRestaurantForCache = (restaurant, { underPressure = false } = {}) => {
    if (!restaurant || typeof restaurant !== 'object' || Array.isArray(restaurant)) {
        return null;
    }

    const slim = omit(restaurant, RESTAURANT_DROP_KEYS);
    const placeDropKeys = underPressure
        ? [...PLACE_DROP_KEYS, ...PLACE_PRESSURE_DROP_KEYS]
        : PLACE_DROP_KEYS;

    if (Array.isArray(slim.places)) {
        slim.places = slim.places.map((place) => (
            place && typeof place === 'object' && !Array.isArray(place)
                ? omit(place, placeDropKeys)
                : place
        ));
    }

    return slim;
};

/**
 * Last-resort projection: the identity and scoping fields without which the app
 * cannot pick a store at all. Everything else is re-fetched on the next sync.
 *
 * Kept deliberately small (a handful of scalars plus one place stub per place),
 * so it fits even on a storage backend that is essentially full.
 *
 * @param {Object|null|undefined} restaurant Raw or slimmed store document.
 * @returns {Object|null} Minimal store record, or `null` for non-object input.
 */
export const coreRestaurantForCache = (restaurant) => {
    if (!restaurant || typeof restaurant !== 'object' || Array.isArray(restaurant)) {
        return null;
    }

    const core = {
        _id: restaurant._id,
        name: restaurant.name,
        enabled: restaurant.enabled,
        active: restaurant.active,
        placeType: restaurant.placeType,
        activeModules: restaurant.activeModules,
        enabledModuleIds: restaurant.enabledModuleIds,
        enabledModes: restaurant.enabledModes,
        localization: restaurant.localization,
        vatNo: restaurant.vatNo,
        logo: restaurant.logo,
        photo: restaurant.photo,
        innerId: restaurant.innerId,
    };

    if (Array.isArray(restaurant.places)) {
        core.places = restaurant.places.map((place) => (
            place && typeof place === 'object' && !Array.isArray(place)
                ? {
                    _id: place._id,
                    name: place.name,
                    raw: place.raw,
                    isOpen: place.isOpen,
                    isTest: place.isTest,
                    vatNo: place.vatNo,
                    enabledModes: place.enabledModes,
                }
                : place
        ));
    }

    // Drop the undefined holes so the stored JSON stays as small as it looks.
    Object.keys(core).forEach((key) => {
        if (typeof core[key] === 'undefined') {
            delete core[key];
        }
    });

    return core;
};

/**
 * Persist the active store document.
 *
 * Writes the slim projection, and on a full storage backend walks down a
 * degradation ladder — slim → slim without table maps → identity core — each
 * step preceded by a cache-eviction pass. Never throws: login, the store
 * switcher and the background refresh all `await` this, and a cache write must
 * not be able to abort any of them.
 *
 * @param {Object|null|undefined} restaurant Raw store document to cache.
 * @returns {Promise<boolean>} true when storage holds a value for `restaurant`
 *   afterwards (written, unchanged, or degraded).
 */
export const persistRestaurant = async (restaurant) => {
    const slim = slimRestaurantForCache(restaurant);

    if (!slim) {
        // An explicit null/undefined store means "no active store" — store JSON
        // null so readers get a parseable value rather than a stale one.
        if (restaurant === null || typeof restaurant === 'undefined') {
            const { ok } = await safeSetItem(RESTAURANT_KEY, 'null', { label: RESTAURANT_KEY });

            return ok;
        }

        return false;
    }

    let serialized = null;

    try {
        serialized = JSON.stringify(slim);
    } catch (error) {
        console.warn('[restaurantCache] could not serialize the store document:', error?.message || error);

        return false;
    }

    const { ok } = await safeSetItem(RESTAURANT_KEY, serialized, {
        label: RESTAURANT_KEY,
        fallbacks: [
            () => JSON.stringify(slimRestaurantForCache(restaurant, { underPressure: true })),
            () => JSON.stringify(coreRestaurantForCache(restaurant)),
        ],
    });

    return ok;
};

/**
 * Read the cached store document.
 *
 * @returns {Promise<Object|null>} The cached store, or `null` when absent/corrupt.
 */
export const readCachedRestaurant = async () => {
    try {
        const raw = await AsyncStorage.getItem(RESTAURANT_KEY);
        const parsed = raw ? JSON.parse(raw) : null;

        return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : null;
    } catch (error) {
        console.warn('[restaurantCache] could not read the cached store:', error?.message || error);

        return null;
    }
};

/**
 * Apply the same projection to every store embedded in a user payload.
 *
 * `HG_USER_INFO` stores the whole `/api/user/me` response, whose `restaurants[]`
 * carries the identical bloat — it is written immediately before `restaurant` on
 * every login, so leaving it raw both wastes the same megabytes and is what
 * pushes the following `restaurant` write over the edge.
 *
 * @param {Object|null|undefined} user Full user payload.
 * @returns {Object|null|undefined} The user with slimmed `restaurants[]`, or the
 *   input unchanged when it is not an object.
 */
export const slimUserRestaurants = (user) => {
    if (!user || typeof user !== 'object' || Array.isArray(user) || !Array.isArray(user.restaurants)) {
        return user;
    }

    return {
        ...user,
        restaurants: user.restaurants.map((entry) => slimRestaurantForCache(entry) || entry),
    };
};

export default persistRestaurant;
